import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiGet, apiPost } from '../../utils/api';
import { Card, Spinner, Badge, Button } from '../design-system';
import { UserPlus, Check, X, Clock, Mail, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';

const JoinRequestsList = ({ onRequestUpdate }) => {
  const { userInfo } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/.netlify/functions/get-join-requests');
      if (response.requests) {
        setRequests(response.requests);
      }
    } catch (error) {
      console.error('Error fetching join requests:', error);
      if (error.message && !error.message.includes('Only practice admins')) {
        toast.error('Failed to load join requests');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setProcessing(prev => ({ ...prev, [requestId]: 'approving' }));
      await apiPost('/.netlify/functions/approve-join-request', {
        requestId: requestId
      });
      toast.success('Join request approved');
      fetchRequests();
      if (onRequestUpdate) {
        onRequestUpdate();
      }
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.message || 'Failed to approve request');
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this join request?')) {
      return;
    }

    try {
      setProcessing(prev => ({ ...prev, [requestId]: 'rejecting' }));
      await apiPost('/.netlify/functions/reject-join-request', {
        requestId: requestId
      });
      toast.success('Join request rejected');
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.message || 'Failed to reject request');
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: null }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="large" />
      </div>
    );
  }

  if (!userInfo?.practice?.isAdmin) {
    return (
      <Card variant="default" padding="large">
        <div className="text-center py-8">
          <p className="text-gray-600">Only practice admins can view join requests</p>
        </div>
      </Card>
    );
  }

  if (requests.length === 0) {
    return (
      <Card variant="default" padding="large">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Requests</h3>
          <p className="text-gray-600">
            All join requests have been processed. New requests will appear here.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          {requests.length} pending {requests.length === 1 ? 'request' : 'requests'}
        </p>
        <Button
          variant="ghost"
          size="small"
          onClick={fetchRequests}
        >
          Refresh
        </Button>
      </div>

      <div className="space-y-3">
        {requests.map((request) => (
          <Card 
            key={request.requestId} 
            variant="default" 
            padding="medium"
            className="border-l-4 border-l-orange-500 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="p-3 bg-orange-100 rounded-lg flex-shrink-0">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-semibold text-gray-900">{request.userName}</p>
                    <Badge variant="default" size="small">
                      Pending
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{request.userEmail}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    <Calendar className="h-3 w-3" />
                    <span>
                      Requested {new Date(request.requestedAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })} at {new Date(request.requestedAt).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                  {request.message && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Message
                      </p>
                      <p className="text-sm text-gray-700 italic">
                        "{request.message}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="success"
                  size="small"
                  onClick={() => handleApprove(request.requestId)}
                  disabled={!!processing[request.requestId]}
                  className="min-w-[100px]"
                >
                  {processing[request.requestId] === 'approving' ? (
                    <>
                      <Spinner size="small" />
                      <span className="ml-2">Approving...</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </>
                  )}
                </Button>
                <Button
                  variant="error"
                  size="small"
                  onClick={() => handleReject(request.requestId)}
                  disabled={!!processing[request.requestId]}
                  className="min-w-[100px]"
                >
                  {processing[request.requestId] === 'rejecting' ? (
                    <>
                      <Spinner size="small" />
                      <span className="ml-2">Rejecting...</span>
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-1" />
                      Reject
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default JoinRequestsList;
